import React, { useState, useContext } from 'react';
import { Container, Form, Button, Row, Col, Card, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faMobile, faMoneyBillWave, faCalendarAlt, faClock, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import CartContext from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import PaystackPayment from '../components/PaystackPayment';

const CheckoutPage = () => {
  const { 
    cartItems, 
    clearCart, 
    totalPrice, 
    updateDeliveryAddress, 
    getCurrentDeliveryZone, 
    freeDeliveryThreshold,
    // deliveryFee,
    // taxAmount,
    // taxRate
  } = useContext(CartContext);
  
  const { api } = useAuth();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [deliveryOption, setDeliveryOption] = useState('now');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [orderData, setOrderData] = useState(null);
  
  const safeTotalPrice = totalPrice || {
    subtotal: 0,
    taxAmount: 0,
    taxRate: 0,
    deliveryFee: 0,
    totalPrice: 0
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    deliveryDate: '',
    deliveryTime: '',
    notes: ''
  });

  // Rely on AuthContext / ProtectedRoute to handle redirects.
  // If you still want an extra check here, use the context's user/loading values instead.
  // (No direct localStorage checks to avoid race conditions with AuthContext initialization.)

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e) => {
    const address = e.target.value;
    setFormData(prev => ({ ...prev, address }));
    updateDeliveryAddress(address);
  };  

  const currentZone = getCurrentDeliveryZone();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Basic validation
    if (!formData.name || !formData.phone || !formData.email || !formData.address) {
      setError('Please fill out all required fields');
      setIsSubmitting(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    // Schedule delivery validation
    if (deliveryOption === 'schedule' && (!formData.deliveryDate || !formData.deliveryTime)) {
      setError('Please select both date and time for scheduled delivery');
      setIsSubmitting(false);
      return;
    }

    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      
      if (!token || token === 'null' || token === 'undefined') {
        throw new Error('Authentication token is missing. Please login again.');
      }

      // Prepare order data
      const orderData = {
        items: cartItems,
        total: safeTotalPrice.totalPrice,
        subtotal: safeTotalPrice.subtotal,
        taxAmount: safeTotalPrice.taxAmount,
        taxRate: safeTotalPrice.taxRate,
        deliveryFee: safeTotalPrice.deliveryFee,
        shippingAddress: formData.address,
        phone: formData.phone,
        customerName: formData.name,
        email: formData.email,
        paymentMethod: paymentMethod,
        deliveryNotes: formData.notes,
        scheduledDelivery: deliveryOption === 'schedule' ? {
          date: formData.deliveryDate,
          time: formData.deliveryTime
        } : null
      };

      // If payment method is card, show payment modal
      if (paymentMethod === 'card') {
        setOrderData(orderData);
        setShowPaymentModal(true);
        setIsSubmitting(false);
        return;
      }

      // For other payment methods, proceed directly
      await createOrder(orderData);

    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.response?.data?.message || error.message || 'Failed to place order. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Separate function to create order
  const createOrder = async (orderData) => {
    try {
      const response = await api.post('/orders', orderData);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Order creation failed');
      }

      // Clear cart and navigate to success page
      clearCart();
      navigate('/order-success', { 
        state: { 
          order: response.data.order,
          message: 'Order placed successfully!' 
        } 
      });
    } catch (error) {
      throw error;
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = async (paymentResponse) => {
    try {
      setIsSubmitting(true);
      
      // Update order data with payment reference
      const orderWithPayment = {
        ...orderData,
        paymentReference: paymentResponse.reference,
        paymentStatus: 'paid'
      };

      await createOrder(orderWithPayment);
      setShowPaymentModal(false);
      
    } catch (error) {
      console.error('Payment success error:', error);
      setError('Payment was successful but order creation failed. Please contact support.');
      setShowPaymentModal(false);
    }
  };

  // Handle payment modal close
  const handlePaymentClose = () => {
    setShowPaymentModal(false);
    setIsSubmitting(false);
  };

  if (cartItems.length === 0) {
    return (
      <Container className="my-5 text-center">
        <Alert variant="info">
          <h4>Your cart is empty</h4>
          <Button variant="danger" href="/menu" className="mt-2">
            Browse Menu
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-4 my-md-5">
      <h1 className="mb-4">Complete Your Order</h1>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      <Form onSubmit={handleSubmit}>
        <Row>
          {/* Delivery Information */}
          <Col lg={7} className="mb-4">
            <Card className="shadow-sm mb-4">
              <Card.Body>
                <Card.Title className="mb-4">Delivery Information</Card.Title>
                
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group controlId="name">
                      <Form.Label>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your full name"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group controlId="phone">
                      <Form.Label>Phone Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="Enter your phone number"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={12}>
                    <Form.Group controlId="email">
                      <Form.Label>Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter your email address"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={12}>
                    <Form.Group controlId="address">
                      <Form.Label>Delivery Address *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="address"
                        value={formData.address}
                        onChange={handleAddressChange}
                        required
                        placeholder="Enter your complete delivery address"
                      />
                      {formData.address && currentZone && (
                        <Form.Text className="text-muted">
                          Delivery to {currentZone.name}: ₦{currentZone.deliveryFee?.toLocaleString()}
                          {safeTotalPrice.subtotal >= freeDeliveryThreshold && (
                            <span className="text-success"> • Free delivery applied!</span>
                          )}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  
                  <Col md={12}>
                    <Form.Group controlId="deliveryOption">
                      <Form.Label>Delivery Option *</Form.Label>
                      <div className="d-flex gap-3 mb-3">
                        <Button
                          variant={deliveryOption === 'now' ? 'danger' : 'outline-secondary'}
                          onClick={() => setDeliveryOption('now')}
                          type="button"
                        >
                          <FontAwesomeIcon icon={faClock} className="me-2" />
                          Deliver Now
                        </Button>
                        <Button
                          variant={deliveryOption === 'schedule' ? 'danger' : 'outline-secondary'}
                          onClick={() => setDeliveryOption('schedule')}
                          type="button"
                        >
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                          Schedule Delivery
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                  
                  {deliveryOption === 'schedule' && (
                    <>
                      <Col md={6}>
                        <Form.Group controlId="deliveryDate">
                          <Form.Label>Delivery Date *</Form.Label>
                          <Form.Control
                            type="date"
                            name="deliveryDate"
                            value={formData.deliveryDate}
                            onChange={handleChange}
                            min={new Date().toISOString().split('T')[0]}
                            required={deliveryOption === 'schedule'}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="deliveryTime">
                          <Form.Label>Delivery Time *</Form.Label>
                          <Form.Control
                            type="time"
                            name="deliveryTime"
                            value={formData.deliveryTime}
                            onChange={handleChange}
                            required={deliveryOption === 'schedule'}
                          />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                  
                  <Col md={12}>
                    <Form.Group controlId="notes">
                      <Form.Label>Delivery Notes (Optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder="Building description, gate code, etc."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            
            {/* Payment Method */}
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title className="mb-4">Payment Method</Card.Title>
                
                <Form.Group>
                  <div className="d-flex flex-column gap-2">
                    <Button
                      variant={paymentMethod === 'card' ? 'danger' : 'outline-secondary'}
                      className="text-start py-3"
                      onClick={() => setPaymentMethod('card')}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                      Credit/Debit Card
                    </Button>
                    
                    <Button
                      variant={paymentMethod === 'transfer' ? 'danger' : 'outline-secondary'}
                      className="text-start py-3"
                      onClick={() => setPaymentMethod('transfer')}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faMobile} className="me-2" />
                      Bank Transfer
                    </Button>
                    
                    <Button
                      variant={paymentMethod === 'cash_on_delivery' ? 'danger' : 'outline-secondary'}
                      className="text-start py-3"
                      onClick={() => setPaymentMethod('cash_on_delivery')}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                      Pay on Delivery (Cash or POS)
                    </Button>
                  </div>
                  
                  {paymentMethod === 'card' && (
                    <Alert variant="info" className="mt-3">
                      You'll be redirected to a secure payment gateway
                    </Alert>
                  )}
                  
                  {paymentMethod === 'transfer' && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <h6>Bank Transfer Details</h6>
                      <p className="mb-1"><strong>Bank:</strong> GTBank</p>
                      <p className="mb-1"><strong>Account Name:</strong> Noureesh Foods</p>
                      <p className="mb-1"><strong>Account Number:</strong> 0123456789</p>
                      <p className="mb-0"><small>Please include your order number as reference</small></p>
                    </div>
                  )}
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Order Summary */}
          <Col lg={5}>
            <Card className="shadow-sm sticky-top" style={{ top: '76px', zIndex: 0 }}>
              <Card.Body>
                <Card.Title className="mb-4">Order Summary</Card.Title>
                
                <div className="mb-3">
                  {cartItems.map(item => (
                    <div key={item._id} className="d-flex justify-content-between py-2 border-bottom">
                      <div>
                        <h6 className="mb-0">{item.name}</h6>
                        <small className="text-muted">₦{item.price.toLocaleString()} × {item.quantity}</small>
                      </div>
                      <div>₦{(item.price * item.quantity).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between py-2">
                    <span className="fs-5">Subtotal</span>
                    <span className="fs-5">₦{safeTotalPrice.subtotal.toLocaleString()}</span>
                  </div>
                  
                  {/* Show delivery fee */}
                  <div className="d-flex justify-content-between py-2">
                    <span className="fs-5">Delivery Fee</span>
                    <span className="fs-5">
                      {safeTotalPrice.deliveryFee === 0 ? (
                        <span className="text-success">Free</span>
                      ) : (
                        `₦${safeTotalPrice.deliveryFee.toLocaleString()}`
                      )}
                    </span>
                  </div>
                  
                  {/* Show tax if applicable */}
                  {safeTotalPrice.taxRate > 0 && (
                    <div className="d-flex justify-content-between py-2">
                      <span className="fs-5">Tax ({safeTotalPrice.taxRate}%)</span>
                      <span className="fs-5">₦{safeTotalPrice.taxAmount.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {/* Show free delivery message if applicable */}
                  {safeTotalPrice.deliveryFee === 0 && safeTotalPrice.subtotal >= freeDeliveryThreshold && (
                    <div className="text-success small mb-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                      Free delivery on orders over ₦{freeDeliveryThreshold.toLocaleString()}
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between py-2 border-top mt-2">
                    <h4>Total</h4>
                    <h4>₦{safeTotalPrice.totalPrice.toLocaleString()}</h4>
                  </div>
                </div>
                
                <Button
                  variant="danger"
                  size="lg"
                  className="w-100 py-3"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Placing Order...' : 'Place Order'}
                </Button>
                
                <p className="small text-muted mt-2">
                  By placing your order, you agree to our terms and conditions
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
      
      {/* Payment Modal */}
      <Modal show={showPaymentModal} onHide={handlePaymentClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Complete Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Total Amount: <strong>₦{safeTotalPrice.totalPrice.toLocaleString()}</strong></p>
          <PaystackPayment
            amount={safeTotalPrice.totalPrice}
            email={formData.email}
            onSuccess={handlePaymentSuccess}
            onClose={handlePaymentClose}
            disabled={isSubmitting}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default CheckoutPage;